import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, MessageSquare, HelpCircle, Truck } from 'lucide-react';
import { LogoSmall } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { TurnaroundNote } from '../components/TurnaroundBadge';
import { CONTACT } from '../lyphline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [loading, setLoading] = useState(false);
  
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    province: '',
    message: ''
  });

  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    province: '',
    product_interest: '',
    budget: '',
    timeline: '',
    message: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/contact`, contactForm);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', phone: '', city: '', province: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/inquiry`, inquiryForm);
      toast.success('Inquiry submitted! Our team will contact you shortly.');
      setInquiryForm({ name: '', email: '', phone: '', city: '', province: '', product_interest: '', budget: '', timeline: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SEO title="Contact LyphLine Freeze Dryers" description="Talk to LyphLine's freeze dryer rebuild team. Call 1-888-223-1435 or request a free diagnostic quote for your machine — serving Canada and the USA." />
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-24 bg-gradient-to-br from-[#003456] to-[#002B47] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-16 w-96 h-96 bg-[#CE8946] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[#CE8946] text-[#003456] px-4 py-2 rounded-md text-sm font-semibold mb-6">
            <Phone className="h-4 w-4" /> Talk to a real technician
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Get In Touch
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Freeze dryer acting up? Send a message below or start a free diagnostic quote and we'll get you back online.
          </p>
        </div>
      </section>

      <div className="py-16 section-padding -mt-10 relative z-10">
        <div className="max-w-7xl mx-auto">

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8 animate-fade-in">
              <div className="bg-white rounded-2xl p-8">
                <h3 className="text-xl font-bold text-[#0a0203] mb-6">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#CE8946]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-[#003456]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0a0203]">Email</p>
                      <a href={`mailto:${CONTACT.email}`} className="text-gray-600 hover:text-[#003456]">
                        {CONTACT.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#CE8946]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-[#003456]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0a0203]">Phone</p>
                      <a href={CONTACT.phoneHref} className="text-gray-600 hover:text-[#003456]">
                        {CONTACT.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#CE8946]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-[#003456]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0a0203]">Address</p>
                      <p className="text-gray-600">
                        {CONTACT.address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#003456] rounded-2xl p-8 text-white">
                <HelpCircle className="h-8 w-8 mb-4 text-[#CE8946]" />
                <h3 className="text-xl font-bold mb-2">Machine down right now?</h3>
                <p className="text-white/80 text-sm mb-4">
                  Skip the back and forth. Start a free diagnostic quote and our rebuild team will assess your freeze dryer and send a firm estimate.
                </p>
                <div className="inline-flex items-center gap-1.5 bg-[#CE8946]/20 text-[#CE8946] text-xs font-semibold px-3 py-1.5 rounded-full mb-4" data-testid="turnaround-badge">
                  <Truck className="h-3.5 w-3.5" /> Emergency shipping available
                </div>
                <Link
                  to="/repair/quote"
                  className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-5 py-2.5 rounded-md transition-colors"
                  data-testid="contact-diagnostic-cta"
                >
                  Get a Free Diagnostic Quote →
                </Link>
                <TurnaroundNote className="text-white/60 mt-4" />
              </div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2 animate-slide-in-right">
              <div className="bg-white rounded-2xl p-8 md:p-12">
                {/* Tab Switcher */}
                <div className="flex gap-4 mb-8">
                  <button
                    onClick={() => setActiveTab('contact')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-all ${
                      activeTab === 'contact'
                        ? 'bg-[#003456] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="contact-tab"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Quick Contact
                  </button>
                  <button
                    onClick={() => setActiveTab('inquiry')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-all ${
                      activeTab === 'inquiry'
                        ? 'bg-[#003456] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="inquiry-tab"
                  >
                    <HelpCircle className="h-4 w-4" />
                    Detailed Inquiry
                  </button>
                </div>

                {/* Contact Form */}
                {activeTab === 'contact' && (
                  <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Name *</label>
                        <Input
                          required
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="John Doe"
                          className="input-focus"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Email *</label>
                        <Input
                          type="email"
                          required
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="john@example.com"
                          className="input-focus"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Phone (optional)</label>
                      <Input
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                        className="input-focus"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">City</label>
                        <Input
                          value={contactForm.city}
                          onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })}
                          placeholder="Toronto"
                          className="input-focus"
                          data-testid="contact-city"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Province/State</label>
                        <Input
                          value={contactForm.province}
                          onChange={(e) => setContactForm({ ...contactForm, province: e.target.value })}
                          placeholder="Ontario"
                          className="input-focus"
                          data-testid="contact-province"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Message *</label>
                      <Textarea
                        required
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        placeholder="How can we help you?"
                        rows={5}
                        className="input-focus"
                        data-testid="contact-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#003456] hover:bg-[#023F63] text-white py-6 btn-scale"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      ) : (
                        <>
                          Send Message <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                )}

                {/* Inquiry Form */}
                {activeTab === 'inquiry' && (
                  <form onSubmit={handleInquirySubmit} className="space-y-6" data-testid="inquiry-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Name *</label>
                        <Input
                          required
                          value={inquiryForm.name}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                          placeholder="John Doe"
                          className="input-focus"
                          data-testid="inquiry-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Email *</label>
                        <Input
                          type="email"
                          required
                          value={inquiryForm.email}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                          placeholder="john@example.com"
                          className="input-focus"
                          data-testid="inquiry-email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Phone *</label>
                      <Input
                        required
                        value={inquiryForm.phone}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                        className="input-focus"
                        data-testid="inquiry-phone"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">City</label>
                        <Input
                          value={inquiryForm.city}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, city: e.target.value })}
                          placeholder="Toronto"
                          className="input-focus"
                          data-testid="inquiry-city"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Province/State</label>
                        <Input
                          value={inquiryForm.province}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, province: e.target.value })}
                          placeholder="Ontario"
                          className="input-focus"
                          data-testid="inquiry-province"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Machine Type *</label>
                      <Select
                        value={inquiryForm.product_interest}
                        onValueChange={(value) => setInquiryForm({ ...inquiryForm, product_interest: value })}
                      >
                        <SelectTrigger className="input-focus" data-testid="inquiry-product">
                          <SelectValue placeholder="Select your machine type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Residential / home freeze dryer">Residential / home freeze dryer</SelectItem>
                          <SelectItem value="Small & medium business unit">Small &amp; medium business unit</SelectItem>
                          <SelectItem value="Commercial / production unit">Commercial / production unit</SelectItem>
                          <SelectItem value="Not sure - need help identifying">Not sure - need help identifying</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Service Needed *</label>
                        <Select
                          value={inquiryForm.budget}
                          onValueChange={(value) => setInquiryForm({ ...inquiryForm, budget: value })}
                        >
                          <SelectTrigger className="input-focus" data-testid="inquiry-budget">
                            <SelectValue placeholder="What do you need?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Diagnostic">Diagnostic</SelectItem>
                            <SelectItem value="Rebuild">Rebuild</SelectItem>
                            <SelectItem value="Modification / upgrade">Modification / upgrade</SelectItem>
                            <SelectItem value="Preventive maintenance">Preventive maintenance</SelectItem>
                            <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Timeline *</label>
                        <Select
                          value={inquiryForm.timeline}
                          onValueChange={(value) => setInquiryForm({ ...inquiryForm, timeline: value })}
                        >
                          <SelectTrigger className="input-focus" data-testid="inquiry-timeline">
                            <SelectValue placeholder="When do you need it?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ASAP">ASAP</SelectItem>
                            <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                            <SelectItem value="1-3 months">1-3 months</SelectItem>
                            <SelectItem value="Just researching">Just researching</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Additional Information (optional)</label>
                      <Textarea
                        value={inquiryForm.message}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                        placeholder="Tell us more about your needs..."
                        rows={4}
                        className="input-focus"
                        data-testid="inquiry-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#003456] hover:bg-[#023F63] text-white py-6 btn-scale"
                      data-testid="inquiry-submit"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      ) : (
                        <>
                          Submit Inquiry <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
